import React from "react"
import {Box, IconButton, Typography, Chip, Button, TextField} from "@mui/material"
import {useFormContext, useWatch} from "react-hook-form"
import CloseIcon from "@mui/icons-material/Close"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"

import {
	OfferFormDataV2,
	discountRateSchema,
	maxTimeToPayWeeksSchema,
	validateTierAgainstContext,
} from "./offerFormValidation"
import {DiscountSummary} from "./DiscountSummary"

export function PaymentPlanTierCard({
	tierId,
	index,
	onDelete,
	balanceInCents,
}: {
	tierId: string
	index: number
	onDelete: () => void
	balanceInCents: number
}) {
	const {
		control,
		trigger,
		setValue,
		clearErrors,
		formState: {errors},
	} = useFormContext<OfferFormDataV2>()
	const watchPaymentPlanTiers = useWatch({control, name: "paymentPlan.tiers"}) ?? []
	const watchOneOff = useWatch({control, name: "oneOff"})

	const tierIndex = watchPaymentPlanTiers.findIndex((tier) => tier.id === tierId)
	const watchTier = watchPaymentPlanTiers[tierIndex]

	const editing = watchTier?.isEditing === true
	const [tempMaxWeeks, setTempMaxWeeks] = React.useState<number | "">(watchTier?.maxTimeToPayWeeks || "")
	const [tempDiscountRate, setTempDiscountRate] = React.useState<number | "">(watchTier?.discountRate || "")
	const [localWeeksError, setLocalWeeksError] = React.useState<string | null>(null)
	const [localDiscountError, setLocalDiscountError] = React.useState<string | null>(null)

	const formWeeksError = errors.paymentPlan?.tiers?.[tierIndex]?.maxTimeToPayWeeks?.message
	const formDiscountError = errors.paymentPlan?.tiers?.[tierIndex]?.discountRate?.message

	const weeksError = localWeeksError || (!editing && formWeeksError) || null
	const discountError = localDiscountError || (!editing && formDiscountError) || null

	const startEditing = () => {
		if (watchTier) {
			const updatedTiers = [...watchPaymentPlanTiers]
			updatedTiers[tierIndex] = {...watchTier, isEditing: true}
			setValue("paymentPlan.tiers", updatedTiers)
		}
	}

	const stopEditing = () => {
		if (watchTier) {
			const updatedTiers = [...watchPaymentPlanTiers]
			updatedTiers[tierIndex] = {...watchTier, isEditing: false}
			setValue("paymentPlan.tiers", updatedTiers)
		}
	}

	const handleConfirm = async () => {
		const weeksResult = maxTimeToPayWeeksSchema.safeParse(tempMaxWeeks === "" ? undefined : tempMaxWeeks)
		const discountResult = discountRateSchema.safeParse(tempDiscountRate === "" ? undefined : tempDiscountRate)

		let weeksErr = weeksResult.success ? null : weeksResult.error.issues[0]?.message || "Invalid value"
		let discountErr = discountResult.success ? null : discountResult.error.issues[0]?.message || "Invalid value"

		if (weeksResult.success && discountResult.success) {
			const newTier = {discountRate: discountResult.data, maxTimeToPayWeeks: weeksResult.data}
			const tiersForValidation = watchPaymentPlanTiers.map((t, i) => (i === tierIndex ? newTier : t))

			const crossValidation = validateTierAgainstContext(newTier, tierIndex, tiersForValidation, watchOneOff)
			weeksErr = crossValidation.weeksError
			discountErr = crossValidation.discountError
		}

		setLocalWeeksError(weeksErr)
		setLocalDiscountError(discountErr)

		if (weeksErr || discountErr) {
			return
		}

		const updatedTiers = [...watchPaymentPlanTiers]
		updatedTiers[tierIndex] = {
			id: tierId,
			discountRate: discountResult.data!,
			maxTimeToPayWeeks: weeksResult.data!,
			isEditing: false,
		}
		setValue("paymentPlan.tiers", updatedTiers)
		clearErrors("offerTypes")
		clearErrors("paymentPlan")
		await trigger("paymentPlan")
	}

	const handleCancel = () => {
		if (watchTier && !watchTier.maxTimeToPayWeeks && !watchTier.discountRate) {
			onDelete()
		} else {
			setTempMaxWeeks(watchTier?.maxTimeToPayWeeks || "")
			setTempDiscountRate(watchTier?.discountRate || "")
			setLocalWeeksError(null)
			setLocalDiscountError(null)
			stopEditing()
		}
	}

	if (!watchTier) {
		return null
	}

	return (
		<Box
			sx={{
				backgroundColor: editing ? "white" : "#ECF0FE",
				border: "1px solid #91A2F3",
				borderRadius: "0.25rem",
				p: "1rem",
				position: "relative",
			}}
		>
			<Box display="flex" flexDirection="column" gap="1.5rem">
				<Box display="flex" alignItems="center" gap="0.5rem">
					<Box
						sx={{
							width: "1.25rem",
							height: "1.25rem",
							borderRadius: "50%",
							backgroundColor: "#7c3aed",
							color: "white",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "0.625rem",
							fontWeight: 600,
						}}
					>
						{index + 1}
					</Box>
					<Typography sx={{fontSize: "0.875rem", fontWeight: 500, color: "rgba(0,0,0,0.87)"}}>
						PP - Option {index + 1}
					</Typography>
					{!editing && (
						<Chip
							label="Confirmed"
							size="small"
							sx={{
								backgroundColor: "white",
								border: "1px solid #474CDE",
								fontSize: "0.75rem",
								height: "1.5rem",
							}}
						/>
					)}
				</Box>
				<Box display="grid" gridTemplateColumns="1fr 1fr" gap="0.75rem">
					<TextField
						label="Up to (weeks)"
						type="number"
						fullWidth
						value={tempMaxWeeks}
						onChange={(e) => {
							setTempMaxWeeks(e.target.value === "" ? "" : Number(e.target.value))
							setLocalWeeksError(null)
						}}
						disabled={!editing}
						error={!!weeksError}
						helperText={weeksError}
						slotProps={{
							input: {sx: {backgroundColor: editing ? "white" : "#ECF0FE"}},
							htmlInput: {min: 1, step: 1},
						}}
					/>
					<TextField
						label="Discount %"
						type="number"
						fullWidth
						value={tempDiscountRate}
						onChange={(e) => {
							setTempDiscountRate(e.target.value === "" ? "" : Number(e.target.value))
							setLocalDiscountError(null)
						}}
						disabled={!editing}
						error={!!discountError}
						helperText={discountError}
						slotProps={{
							input: {sx: {backgroundColor: editing ? "white" : "#ECF0FE"}},
							htmlInput: {min: 0, max: 100, step: 1},
						}}
					/>
				</Box>
				{editing && (
					<DiscountSummary balanceInCents={balanceInCents} discountRate={tempDiscountRate} variant="plan" />
				)}
			</Box>
			<IconButton
				size="small"
				onClick={() => {
					if (!editing) {
						startEditing()
					} else {
						onDelete()
					}
				}}
				sx={{position: "absolute", top: "0.5rem", right: "0.5rem", color: "rgba(0,0,0,0.54)"}}
			>
				{!editing ? <EditOutlinedIcon sx={{fontSize: "1rem"}} /> : <CloseIcon />}
			</IconButton>
			<Box sx={{borderTop: "1px solid #E9EAEB", pt: "0.5rem", mt: "1rem"}}>
				<Typography sx={{fontSize: "0.75rem", color: "#535862"}}>
					Customer receives <strong>{tempDiscountRate || 0}% discount</strong> if they complete payment up to{" "}
					{tempMaxWeeks || 0} weeks
				</Typography>
			</Box>
			{editing && (
				<Box display="flex" gap="0.5rem" justifyContent="flex-end" mt="1rem">
					<Button
						variant="outlined"
						size="small"
						onClick={handleCancel}
						sx={{
							textTransform: "uppercase",
							borderColor: "#474CDE",
							color: "#474CDE",
							backgroundColor: "white",
							"&:hover": {borderColor: "#3539b5", backgroundColor: "rgba(71, 76, 222, 0.04)"},
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						size="small"
						onClick={handleConfirm}
						sx={{
							textTransform: "uppercase",
							backgroundColor: "#474CDE",
							"&:hover": {backgroundColor: "#3539b5"},
						}}
					>
						Confirm
					</Button>
				</Box>
			)}
		</Box>
	)
}
